import { useContext, useEffect, useRef, useState } from 'react';
import { Context } from "../contexts/AppContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { postData, putData } from '../../lib/utils';
import { useParams } from 'react-router-dom';
import UploadImageBg from '../Gallery/UploadImageBg'; 

const StillImage = () => {
  const videoRef = useRef(null);

  const [isUploading, setIsUploading] = useState(false);

  const { value } = useContext(Context);
  const [appState, setAppState] = value;

  const { galleryId } = useParams();

  const [imgUrl, setImgUrl] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);

  // Sync with global state
  useEffect(() => {
    setImgUrl(appState.currentGalleryData?.backgroundMedia?.imgUrl || null);
    setVideoUrl(appState.currentGalleryData?.backgroundMedia?.videoUrl || null);
  }, [
    appState.currentGalleryData?.backgroundMedia?.imgUrl,
    appState.currentGalleryData?.backgroundMedia?.videoUrl
  ]);

  // 🔍 Check image exists
  const checkImageExists = (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = url;
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
    });
  };

  // 🔁 Polling
  const waitForImage = async (url, retries = 10, delay = 800) => {
    for (let i = 0; i < retries; i++) {
      const exists = await checkImageExists(url);
      if (exists) return true;
      await new Promise((res) => setTimeout(res, delay));
    }
    return false;
  };

  // 📸 Upload Success
  const imgUploadedDone = async (file) => {
    setIsUploading(true);

    const imgPath = `${appState?.assetsUrl}/${file.name}`;
    const fullUrl = `${process.env.REACT_APP_VIDEO_DEST_BASE_PATH}${imgPath}?t=${Date.now()}`;

    try {
      const json = await putData(`galleries/gallery/${galleryId}/background`, {
        imgUrl: imgPath
      });

      if (json) {
        const ready = await waitForImage(fullUrl);

        if (ready) {
          setAppState((prev) => ({
            ...prev,
            currentGalleryData: {
              ...prev.currentGalleryData,
              backgroundMedia: {
                ...prev.currentGalleryData.backgroundMedia,
                imgUrl: json.backgroundMedia.imgUrl
              }
            }
          }));

          setImgUrl(json.backgroundMedia.imgUrl);
        } else {
          console.warn("Image not available after retries");
        }
      }
    } catch (err) {
      console.error(err);
      alert("Upload failed");
    }

    setIsUploading(false);
  };

  const imgOnPrg = () => setIsUploading(true);

  const uploadFailed = () => {
    alert("Oops Image Uploading Failed....");
    setIsUploading(false);
  };

  // 🗑 Delete Image
  const deleteBgImg = async () => {
    try {
      const filePath = appState?.currentGalleryData?.backgroundMedia?.imgUrl;

      await postData("products/delete-file", { fileName: filePath });

      const json = await putData(`galleries/gallery/${galleryId}/background`, {
        imgUrl: null
      });

      if (json) {
        setAppState((prev) => ({
          ...prev,
          currentGalleryData: {
            ...prev.currentGalleryData,
            backgroundMedia: {
              ...prev.currentGalleryData.backgroundMedia,
              imgUrl: null
            }
          }
        }));

        setImgUrl(null);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // 🎥 Delete Video
  const deleteVideoBg = async () => {
    try {
      const json = await putData(`galleries/gallery/${galleryId}/background`, {
        videoUrl: null
      });

      if (json) {
        setAppState((prev) => ({
          ...prev,
          currentGalleryData: {
            ...prev.currentGalleryData,
            backgroundMedia: {
              ...prev.currentGalleryData.backgroundMedia,
              videoUrl: null
            }
          }
        }));

        setVideoUrl(null);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const btnHandler = () => {
    setAppState((prev) => ({
      ...prev,
      modalData: {
        ...prev.modalData,
        type: "AddMediaModal",
        isOpen: true,
        openFrom: "videoBg"
      }
    }));
  };

  return (
    <div className='space-y-4'>

      {/* 📸 IMAGE */}
      <div className='border px-4 py-2 bg-[#f0f2f5] h-[290px]'>
        <div>Still Image</div>

        <div className='h-[150px] border flex items-center justify-center'>
          {isUploading ? (
            <FontAwesomeIcon icon={faSpinner} spin size="3x" />
          ) : (
            <img
              className="w-full object-cover h-[150px]"
              src={
                imgUrl
                  ? `${process.env.REACT_APP_VIDEO_DEST_BASE_PATH}${imgUrl}?t=${Date.now()}`
                  : "../../../img/thumbnail/gallery/placeholder-image.jpg"
              }
              alt="preview"
            />
          )}
        </div>

        <div className="text-xs text-gray-400 py-2">
          PNG, JPG, or JPEG (up to 40MB)
        </div>

        <div className='flex items-center'>
          <div className="flex-grow">
            <UploadImageBg  
              imgOnPrg={imgOnPrg} 
              imgUploadedDone={imgUploadedDone} 
              uploadFailed={uploadFailed} 
              type={"galleryBg"}
            />
          </div>

          <FontAwesomeIcon  
            icon={faTrash} 
            onClick={deleteBgImg}
            className="ml-2 cursor-pointer"
            style={{ background:'#1F2937', padding:'10px', color:'#fff' }}
          />
        </div>
      </div>

      {/* 🎥 VIDEO */}
      <div className='border px-4 py-2 bg-[#f0f2f5]'>
        <div>Video Background</div>

        {videoUrl ? (
          <video
            ref={videoRef}
            className="w-full h-[150px] object-cover"
            muted
            onLoadedMetadata={() => {
              videoRef.current.currentTime = 10;
            }}
          >
            <source
              src={`${process.env.REACT_APP_VIDEO_DEST_BASE_PATH}${videoUrl}?t=${Date.now()}`}
              type="video/mp4"
            />
          </video>
        ) : (
          <img
            className="w-full h-[150px] object-cover"
            src="../../../img/thumbnail/gallery/placeholder-image.jpg"
            alt="thumb"
          />
        )}

        <div className="text-xs text-gray-400 py-2">
          16:9 Aspect Ratio up to 30 sec
        </div>

        <div className='flex items-center'>
          <div className="flex-grow">
            <button onClick={btnHandler} className="btn-yellow">
              Video Background
            </button>
          </div>

          <FontAwesomeIcon  
            icon={faTrash} 
            onClick={deleteVideoBg}
            className="ml-2 cursor-pointer"
            style={{ background:'#1F2937', padding:'10px', color:'#fff' }}
          />
        </div>
      </div>

    </div>
  );
};

export default StillImage;